import { useState, useEffect, useCallback, useMemo } from "react";

// ─── TIMEZONES ────────────────────────────────────────────────────────────────
const TIMEZONES = [
  "UTC","America/New_York","America/Chicago","America/Denver","America/Los_Angeles",
  "America/Toronto","America/Vancouver","America/Sao_Paulo","America/Mexico_City",
  "America/Buenos_Aires","Europe/London","Europe/Paris","Europe/Berlin","Europe/Madrid",
  "Europe/Rome","Europe/Amsterdam","Europe/Stockholm","Europe/Moscow","Europe/Istanbul",
  "Africa/Cairo","Africa/Johannesburg","Africa/Lagos","Asia/Dubai","Asia/Kolkata",
  "Asia/Dhaka","Asia/Bangkok","Asia/Singapore","Asia/Shanghai","Asia/Tokyo",
  "Asia/Seoul","Asia/Hong_Kong","Asia/Karachi","Asia/Riyadh","Australia/Sydney",
  "Australia/Melbourne","Australia/Perth","Pacific/Auckland","Pacific/Honolulu",
];

//function fmtDateTime(date) {
//  if (!date || isNaN(date)) return "—";
//  return date.toISOString().replace("T"," ").slice(0,19) + " UTC";
//}

function fmtInTz(date, tz) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year:"numeric", month:"2-digit", day:"2-digit",
      hour:"2-digit", minute:"2-digit", second:"2-digit",
      hour12: false,
    }).format(date);
  } catch { return "Invalid timezone"; }
}

function getOffset(tz) {
  try {
    const now = new Date();
    //const utcMs = now.getTime();
    const local = new Date(now.toLocaleString("en-US", { timeZone: tz }));
    const diff = (local - now) / 60000;
    const sign = diff >= 0 ? "+" : "-";
    const abs  = Math.abs(diff);
    return `UTC${sign}${String(Math.floor(abs/60)).padStart(2,"0")}:${String(abs%60).padStart(2,"0")}`;
  } catch { return "—"; }
}

// ─── COPY BUTTON ─────────────────────────────────────────────────────────────
function CopyBtn({ text, t }) {
  const [copied, setCopied] = useState(false);
  return (
    <button className="btn-sm" onClick={() => navigator.clipboard.writeText(text||"").then(()=>{setCopied(true);setTimeout(()=>setCopied(false),1600);})}
      style={{ borderColor:copied?t.statusOk:t.border, color:copied?t.statusOk:t.textMuted }}>
      {copied?"✓ Copied":"⎘ Copy"}
    </button>
  );
}

// ─── UNIX TIMESTAMP CONVERTER ────────────────────────────────────────────────
function UnixTimestamp({ t }) {
  const [tsInput, setTsInput]   = useState(String(Math.floor(Date.now()/1000)));
  const [dtInput, setDtInput]   = useState("");
  const [unit, setUnit]         = useState("seconds");
  const [liveMode, setLiveMode] = useState(false);
  const [now, setNow]           = useState(Date.now());

  useEffect(() => {
    if (!liveMode) return;
    const id = setInterval(() => { setNow(Date.now()); setTsInput(String(Math.floor(Date.now()/1000))); }, 1000);
    return () => clearInterval(id);
  }, [liveMode]);

  const dateFromTs = useMemo(() => {
    const val = tsInput.trim();
    if (!val || isNaN(+val)) return null;
    const ms = unit === "seconds" ? +val * 1000 : +val;
    return new Date(ms);
  }, [tsInput, unit]);

  const tsFromDt = useMemo(() => {
    if (!dtInput.trim()) return null;
    const d = new Date(dtInput);
    if (isNaN(d)) return null;
    return { s: Math.floor(d.getTime()/1000), ms: d.getTime() };
  }, [dtInput]);

  const infoRows = dateFromTs && !isNaN(dateFromTs) ? [
    { label:"UTC",        val: dateFromTs.toUTCString() },
    { label:"ISO 8601",   val: dateFromTs.toISOString() },
    { label:"Local",      val: dateFromTs.toLocaleString() },
    { label:"Unix (s)",   val: String(Math.floor(dateFromTs.getTime()/1000)) },
    { label:"Unix (ms)",  val: String(dateFromTs.getTime()) },
    { label:"Day of week",val: dateFromTs.toLocaleDateString("en-US",{weekday:"long"}) },
    { label:"Day of year",val: String(Math.floor((dateFromTs - new Date(dateFromTs.getFullYear(),0,0))/86400000)) },
    { label:"Week number",val: String(Math.ceil((((dateFromTs - new Date(dateFromTs.getFullYear(),0,1))/86400000)+1)/7)) },
  ] : [];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      {/* Live clock */}
      <div style={{ background:t.bgChipActive, border:`1px solid ${t.border}`, borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
        <div>
          <div style={{ fontSize:11, color:t.textMuted, marginBottom:2, fontFamily:"JetBrains Mono", textTransform:"uppercase", letterSpacing:"0.07em" }}>Current Unix Timestamp</div>
          <div style={{ fontSize:22, fontWeight:700, color:t.textAccent, fontFamily:"JetBrains Mono" }}>{Math.floor(now/1000)}</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <div onClick={()=>setLiveMode(v=>!v)} style={{ display:"flex", alignItems:"center", gap:7, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"6px 12px", cursor:"pointer" }}>
            <div className={`toggle ${liveMode?"on":""}`}><div className="toggle-thumb"/></div>
            <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Live</span>
          </div>
          <button className="btn-sm" onClick={()=>setTsInput(String(Math.floor(Date.now()/1000)))}>Use Now</button>
        </div>
      </div>

      <div className="grid-2">
        {/* Timestamp → Date */}
        <div className="panel" style={{ border:`1px solid ${tsInput.trim()?t.borderActive:t.border}` }}>
          <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
            Timestamp → Date
          </div>
          <div style={{ padding:"14px", display:"flex", flexDirection:"column", gap:10 }}>
            <input value={tsInput} onChange={e=>setTsInput(e.target.value)} placeholder="1700000000"
              style={{ width:"100%", background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:7, padding:"8px 12px", fontSize:14, fontFamily:"JetBrains Mono", color:t.textPrimary, outline:"none" }} />
            <div style={{ display:"flex", gap:6 }}>
              {["seconds","milliseconds"].map(u => (
                <button key={u} className="btn-sm" onClick={()=>setUnit(u)}
                  style={{ borderColor:unit===u?t.borderActive:t.border, color:unit===u?t.textAccent:t.textMuted, background:unit===u?t.bgChipActive:"none" }}>{u}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Date → Timestamp */}
        <div className="panel">
          <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
            Date → Timestamp
          </div>
          <div style={{ padding:"14px", display:"flex", flexDirection:"column", gap:10 }}>
            <input type="datetime-local" value={dtInput} onChange={e=>setDtInput(e.target.value)}
              style={{ width:"100%", background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:7, padding:"8px 12px", fontSize:13, fontFamily:"JetBrains Mono", color:t.textPrimary, outline:"none", colorScheme: "dark" }} />
            {tsFromDt && (
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {[{label:"Seconds",val:String(tsFromDt.s)},{label:"Milliseconds",val:String(tsFromDt.ms)}].map(r=>(
                  <div key={r.label} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:t.bgPanelHead, borderRadius:7, padding:"7px 10px" }}>
                    <span style={{ fontSize:11, color:t.textMuted, fontFamily:"JetBrains Mono" }}>{r.label}</span>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontFamily:"JetBrains Mono", fontSize:13, color:t.textAccent }}>{r.val}</span>
                      <CopyBtn text={r.val} t={t} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full info table */}
      {infoRows.length > 0 && (
        <div className="panel active-panel">
          <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
            Full Breakdown
          </div>
          <div style={{ display:"flex", flexDirection:"column" }}>
            {infoRows.map((row,i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 14px", borderBottom:`1px solid ${t.border}`, gap:12 }}>
                <span style={{ fontSize:12, color:t.textMuted, fontFamily:"JetBrains Mono", minWidth:100, flexShrink:0 }}>{row.label}</span>
                <span style={{ fontSize:12, color:t.textSecondary, fontFamily:"JetBrains Mono", flex:1, wordBreak:"break-all" }}>{row.val}</span>
                <CopyBtn text={row.val} t={t} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TIMEZONE CONVERTER ───────────────────────────────────────────────────────
function TimezoneConverter({ t }) {
  const [inputDate, setInputDate]   = useState(() => new Date().toISOString().slice(0,16));
  const [fromTz, setFromTz]         = useState("UTC");
  const [selectedZones, setSelectedZones] = useState(["America/New_York","Europe/London","Asia/Tokyo","Asia/Kolkata"]);
  const [addZone, setAddZone]       = useState("");

  const sourceDate = useMemo(() => {
    try { return new Date(inputDate + (inputDate.length === 16 ? ":00" : "")); }
    catch { return null; }
  }, [inputDate]);

  const addTimezone = () => {
    if (addZone && !selectedZones.includes(addZone)) {
      setSelectedZones(prev => [...prev, addZone]);
      setAddZone("");
    }
  };

  const removeZone = (tz) => setSelectedZones(prev => prev.filter(z => z !== tz));

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"8px 12px" }}>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>Source time</span>
          <input type="datetime-local" value={inputDate} onChange={e=>setInputDate(e.target.value)}
            style={{ background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:5, padding:"3px 8px", fontSize:12, fontFamily:"JetBrains Mono", color:t.textPrimary, outline:"none", colorScheme:"dark" }} />
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, background:t.bgSettingGrp, border:`1px solid ${t.border}`, borderRadius:10, padding:"8px 12px" }}>
          <span style={{ fontSize:11, color:t.textLabel, fontFamily:"JetBrains Mono" }}>From</span>
          <select value={fromTz} onChange={e=>setFromTz(e.target.value)}
            style={{ background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:5, padding:"3px 8px", fontSize:11, fontFamily:"JetBrains Mono", color:t.textAccent, outline:"none" }}>
            {TIMEZONES.map(tz=><option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
        <button className="btn-sm" onClick={()=>setInputDate(new Date().toISOString().slice(0,16))}>Now</button>
      </div>

      {/* Add more timezones */}
      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
        <select value={addZone} onChange={e=>setAddZone(e.target.value)}
          style={{ flex:1, background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:7, padding:"7px 10px", fontSize:12, fontFamily:"JetBrains Mono", color:t.textAccent, outline:"none" }}>
          <option value="">Add timezone…</option>
          {TIMEZONES.filter(tz=>!selectedZones.includes(tz)).map(tz=><option key={tz} value={tz}>{tz}</option>)}
        </select>
        <button className="btn-sm" onClick={addTimezone} disabled={!addZone} style={{ padding:"7px 16px" }}>+ Add</button>
      </div>

      {/* Results grid */}
      {sourceDate && !isNaN(sourceDate) && (
        <div style={{ display:"grid", gap:8 }}>
          {/* Source */}
          <div style={{ background:t.bgChipActive, border:`1px solid ${t.borderActive}`, borderRadius:12, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
            <div>
              <div style={{ fontSize:10, color:t.textMuted, marginBottom:3, fontFamily:"JetBrains Mono", textTransform:"uppercase", letterSpacing:"0.07em" }}>{fromTz} (source) · {getOffset(fromTz)}</div>
              <div style={{ fontSize:15, fontWeight:700, color:t.textAccent, fontFamily:"JetBrains Mono" }}>{fmtInTz(sourceDate, fromTz)}</div>
            </div>
            <CopyBtn text={fmtInTz(sourceDate, fromTz)} t={t} />
          </div>

          {selectedZones.map(tz => (
            <div key={tz} className="panel" style={{ padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:10, color:t.textMuted, marginBottom:3, fontFamily:"JetBrains Mono", textTransform:"uppercase", letterSpacing:"0.07em" }}>{tz} · {getOffset(tz)}</div>
                <div style={{ fontSize:14, fontWeight:600, color:t.textPrimary, fontFamily:"JetBrains Mono" }}>{fmtInTz(sourceDate, tz)}</div>
              </div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                <CopyBtn text={fmtInTz(sourceDate, tz)} t={t} />
                <button className="btn-sm" onClick={()=>removeZone(tz)} style={{ color:t.statusErr, borderColor:t.statusErr }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── CRON EXPRESSION GENERATOR ────────────────────────────────────────────────
const CRON_PRESETS = [
  { label:"Every minute",          expr:"* * * * *",     desc:"Runs every minute" },
  { label:"Every 5 minutes",       expr:"*/5 * * * *",   desc:"Runs every 5 minutes" },
  { label:"Every 15 minutes",      expr:"*/15 * * * *",  desc:"Runs every 15 minutes" },
  { label:"Every 30 minutes",      expr:"*/30 * * * *",  desc:"Runs every 30 minutes" },
  { label:"Every hour",            expr:"0 * * * *",     desc:"Runs at the start of every hour" },
  { label:"Every 6 hours",         expr:"0 */6 * * *",   desc:"Runs at midnight, 6am, noon, 6pm" },
  { label:"Every 12 hours",        expr:"0 */12 * * *",  desc:"Runs at midnight and noon" },
  { label:"Every day at midnight", expr:"0 0 * * *",     desc:"Runs once daily at 00:00" },
  { label:"Every day at noon",     expr:"0 12 * * *",    desc:"Runs once daily at 12:00" },
  { label:"Every weekday",         expr:"0 9 * * 1-5",   desc:"Runs at 9am Mon–Fri" },
  { label:"Every weekend",         expr:"0 10 * * 6,0",  desc:"Runs at 10am Sat & Sun" },
  { label:"Every Monday",          expr:"0 8 * * 1",     desc:"Runs at 8am every Monday" },
  { label:"Every week (Sunday)",   expr:"0 0 * * 0",     desc:"Runs at midnight every Sunday" },
  { label:"1st of every month",    expr:"0 0 1 * *",     desc:"Runs at midnight on the 1st" },
  { label:"Every month",           expr:"0 0 1 * *",     desc:"Runs at midnight on the 1st" },
  { label:"Every quarter",         expr:"0 0 1 */3 *",   desc:"Runs at midnight Jan 1, Apr 1, Jul 1, Oct 1" },
  { label:"Every year",            expr:"0 0 1 1 *",     desc:"Runs at midnight on Jan 1st" },
  { label:"Every night at 2am",    expr:"0 2 * * *",     desc:"Common maintenance window" },
];

function parseCron(expr) {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [min, hour, dom, month, dow] = parts;
  const months = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const days   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  function describe(val, unit, names) {
    if (val === "*") return `every ${unit}`;
    if (val.startsWith("*/")) return `every ${val.slice(2)} ${unit}s`;
    if (val.includes("-")) {
      const [a,b] = val.split("-");
      return `${unit}s ${names?names[+a]:a}–${names?names[+b]:b}`;
    }
    if (val.includes(",")) return val.split(",").map(v=>names?names[+v]:v).join(", ");
    return names ? (names[+val] || val) : val;
  }

  return {
    min:   describe(min,   "minute"),
    hour:  describe(hour,  "hour"),
    dom:   describe(dom,   "day of month"),
    month: describe(month, "month", months),
    dow:   describe(dow,   "weekday", days),
    parts,
  };
}

function nextRuns(expr, count = 5) {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return [];
  const [minP, hourP, domP, monthP, dowP] = parts;

  const matchField = (val, n) => {
    if (val === "*") return true;
    if (val.startsWith("*/")) return n % parseInt(val.slice(2)) === 0;
    return val.split(",").some(v => {
      if (v.includes("-")) { const [a,b]=v.split("-"); return n>=+a&&n<=+b; }
      return +v === n;
    });
  };

  const runs = [];
  let d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + 1);
  let attempts = 0;

  while (runs.length < count && attempts < 100000) {
    attempts++;
    if (
      matchField(monthP, d.getMonth()+1) &&
      matchField(domP,   d.getDate()) &&
      matchField(dowP,   d.getDay()) &&
      matchField(hourP,  d.getHours()) &&
      matchField(minP,   d.getMinutes())
    ) { runs.push(new Date(d)); }
    d.setMinutes(d.getMinutes() + 1);
  }
  return runs;
}

function CronGenerator({ t }) {
  const [expr, setExpr]       = useState("0 9 * * 1-5");
  const [parsed, setParsed]   = useState(null);
  const [runs, setRuns]       = useState([]);
  const [error, setError]     = useState(null);

  // Builder state
  const [bMin, setBMin]   = useState("0");
  const [bHour, setBHour] = useState("9");
  const [bDom, setBDom]   = useState("*");
  const [bMonth, setBMonth] = useState("*");
  const [bDow, setBDow]   = useState("1-5");

  useEffect(() => {
    try {
      const p = parseCron(expr);
      if (!p) { setError("Invalid cron expression"); setParsed(null); setRuns([]); return; }
      setParsed(p);
      setRuns(nextRuns(expr));
      setError(null);
    } catch(e) { setError(e.message); }
  }, [expr]);

  const fromBuilder = useCallback(() => {
    setExpr(`${bMin} ${bHour} ${bDom} ${bMonth} ${bDow}`);
  }, [bMin, bHour, bDom, bMonth, bDow]);

  const FIELDS = [
    { label:"Minute (0-59)",   val:bMin,   set:setBMin,   placeholder:"0, *, */5, 0-30" },
    { label:"Hour (0-23)",     val:bHour,  set:setBHour,  placeholder:"9, *, */6, 8-18" },
    { label:"Day of month",    val:bDom,   set:setBDom,   placeholder:"*, 1, 15, 1-7" },
    { label:"Month (1-12)",    val:bMonth, set:setBMonth, placeholder:"*, 1, 6-12" },
    { label:"Day of week (0-6)",val:bDow,  set:setBDow,   placeholder:"*, 1-5, 0,6" },
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      {/* Expression input */}
      <div className="panel" style={{ border:`1px solid ${error?t.borderError:expr.trim()?t.borderActive:t.border}` }}>
        <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color: error?t.statusErr:t.textAccent }}>
          Cron Expression
        </div>
        <div style={{ padding:"14px", display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <input value={expr} onChange={e=>setExpr(e.target.value)}
            style={{ flex:1, minWidth:200, background:t.bgInput, border:`1px solid ${error?t.statusErr:t.border}`, borderRadius:7, padding:"9px 14px", fontSize:16, fontFamily:"JetBrains Mono", fontWeight:600, color:t.textPrimary, outline:"none", letterSpacing:"0.1em" }} />
          <CopyBtn text={expr} t={t} />
        </div>
        {error && <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, color:t.statusErr, fontFamily:"JetBrains Mono" }}>✕ {error}</div>}
      </div>

      {/* Human description */}
      {parsed && (
        <div style={{ background:t.bgChipActive, border:`1px solid ${t.borderActive}`, borderRadius:10, padding:"12px 16px" }}>
          <div style={{ fontSize:11, color:t.textMuted, marginBottom:6, fontFamily:"JetBrains Mono", textTransform:"uppercase", letterSpacing:"0.07em" }}>Runs</div>
          <div style={{ fontSize:14, color:t.textPrimary, lineHeight:1.7 }}>
            At <strong style={{ color:t.textAccent }}>{parsed.min}</strong>{" "}
            of <strong style={{ color:t.textAccent }}>{parsed.hour}</strong>{" "}
            on <strong style={{ color:t.textAccent }}>{parsed.dom}</strong>{" "}
            in <strong style={{ color:t.textAccent }}>{parsed.month}</strong>{" "}
            on <strong style={{ color:t.textAccent }}>{parsed.dow}</strong>
          </div>
        </div>
      )}

      <div className="grid-2" style={{ alignItems:"start" }}>
        {/* Visual builder */}
        <div className="panel">
          <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
            Visual Builder
          </div>
          <div style={{ padding:"14px", display:"flex", flexDirection:"column", gap:10 }}>
            {FIELDS.map(f => (
              <div key={f.label}>
                <div style={{ fontSize:11, color:t.textMuted, marginBottom:4, fontFamily:"JetBrains Mono" }}>{f.label}</div>
                <input value={f.val} onChange={e=>f.set(e.target.value)} placeholder={f.placeholder}
                  style={{ width:"100%", background:t.bgInput, border:`1px solid ${t.border}`, borderRadius:7, padding:"6px 10px", fontSize:12, fontFamily:"JetBrains Mono", color:t.textPrimary, outline:"none" }} />
              </div>
            ))}
            <button className="btn-primary" onClick={fromBuilder} style={{ width:"100%", justifyContent:"center", padding:"8px", marginTop:4 }}>Build Expression →</button>
          </div>
        </div>

        {/* Next runs + presets */}
        <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
          {runs.length > 0 && (
            <div className="panel">
              <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
                Next 5 Runs
              </div>
              {runs.map((d,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 14px", borderBottom:`1px solid ${t.border}` }}>
                  <span style={{ fontSize:11, color:t.textMuted, fontFamily:"JetBrains Mono", minWidth:20 }}>{i+1}</span>
                  <span style={{ fontSize:12, color:t.textSecondary, fontFamily:"JetBrains Mono", flex:1 }}>{d.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}

          {/* Presets */}
          <div className="panel">
            <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
              Common Presets
            </div>
            <div style={{ maxHeight:260, overflowY:"auto", scrollbarWidth:"thin" }}>
              {CRON_PRESETS.map(p => (
                <div key={p.expr} onClick={()=>setExpr(p.expr)}
                  style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 14px", borderBottom:`1px solid ${t.border}`, cursor:"pointer", background: expr===p.expr ? t.bgChipActive : "transparent", transition:"background 0.15s" }}>
                  <div>
                    <div style={{ fontSize:12, color:expr===p.expr?t.textAccent:t.textPrimary, fontWeight: expr===p.expr?600:400 }}>{p.label}</div>
                    <div style={{ fontSize:10, color:t.textMuted, fontFamily:"JetBrains Mono" }}>{p.expr}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const TOOLS = [
  { id:"unix",     label:"Unix Timestamp",    icon:"🕐", desc:"Convert timestamps ↔ dates" },
  { id:"timezone", label:"Timezone Converter",icon:"🌍", desc:"Convert times across timezones" },
  { id:"cron",     label:"Cron Generator",    icon:"⏱", desc:"Build & decode cron expressions" },
];

export default function TimeToolkit({ t }) {
  const [active, setActive] = useState("unix");

  const components = {
    unix:     <UnixTimestamp t={t} />,
    timezone: <TimezoneConverter t={t} />,
    cron:     <CronGenerator t={t} />,
  };

  return (
    <div>
      <div className="tool-hero fade-up">
        <div className="tool-hero-inner">
          <div className="tool-hero-left">
            <h2>🕐 Time & Date Tools</h2>
            <p>Unix timestamp converter, timezone converter, and cron expression generator — all instant, all in your browser.</p>
          </div>
          <div className="tool-hero-stats">
            <div className="tool-stat"><div className="tool-stat-val">3</div><div className="tool-stat-lbl">tools</div></div>
            <div className="tool-stat"><div className="tool-stat-val">{new Intl.DateTimeFormat().resolvedOptions().timeZone}</div><div className="tool-stat-lbl">your timezone</div></div>
          </div>
        </div>
      </div>

      <div className="pill-tab-bar" style={{ display:"flex", gap:6, padding:"12px 16px", flexWrap:"wrap" }}>
        {TOOLS.map(tool => {
          const isActive = active === tool.id;
          return (
            <button key={tool.id} onClick={() => setActive(tool.id)}
              className={`pill-tab ${isActive ? "active" : ""}`}>
              <span>{tool.icon}</span>{tool.label}
            </button>
          );
        })}
      </div>

      <div className="tool-main">
        {components[active]}
      </div>

      <article className="tool-seo-section" aria-label="About Time & Date Tools">
        <h2>About Time &amp; Date Tools</h2>
        <div className="tool-seo-cols">
          <div>
            <h3>How it works</h3>
            <ul>
              <li>Unix Timestamp Converter supports both seconds and milliseconds, with live mode ticking in real time.</li>
              <li>Timezone Converter uses the browser's Intl API to handle 35+ worldwide timezones including DST.</li>
              <li>Cron Expression Generator parses standard 5-field expressions and previews the next 5 execution times.</li>
            </ul>
          </div>
          <div>
            <h3>Use cases</h3>
            <ul>
              <li>Debug API timestamps from logs or responses.</li>
              <li>Schedule cron jobs on servers or cloud functions.</li>
              <li>Coordinate meetings across international timezones.</li>
              <li>Convert human-readable dates to Unix epochs for database queries.</li>
            </ul>
          </div>
          <div>
            <h3>Privacy</h3>
            <ul>
              <li>All conversions run in your browser — no data is sent to any server.</li>
              <li>No sign-up, no account, free to use forever.</li>
            </ul>
          </div>
        </div>
        <h2 style={{ marginTop:"1.5rem" }}>Frequently Asked Questions</h2>
        <details><summary>What is a Unix timestamp?</summary><p>A Unix timestamp is the number of seconds (or milliseconds) elapsed since January 1, 1970 00:00:00 UTC, also called the Unix epoch. It is the standard time representation used in most programming languages, databases, and APIs.</p></details>
        <details><summary>How do I build a cron expression?</summary><p>Use the visual cron builder — set individual fields for minute, hour, day-of-month, month, and day-of-week. The tool instantly shows a plain English description of the schedule and previews the next 5 run times. You can also choose from 18 common presets.</p></details>
        <details><summary>Does the Timezone Converter account for Daylight Saving Time?</summary><p>Yes. The converter uses the browser's built-in Intl.DateTimeFormat API, which correctly applies DST rules for all supported timezones based on the date you select.</p></details>
        <details><summary>What is the difference between seconds and milliseconds in a Unix timestamp?</summary><p>Most systems use seconds (10-digit timestamps like 1745270400). JavaScript's Date.now() returns milliseconds (13-digit). The converter detects which format you've entered automatically, but you can also select the unit manually.</p></details>
        <h2 style={{ marginTop:"1.5rem" }}>Alternatives &amp; why miniDevToolkit</h2>
        <p>Compared to <strong>EpochConverter.com</strong> — miniDevToolkit bundles a cron builder and timezone converter alongside the timestamp converter, replacing three bookmarks with one. Compared to <strong>Crontab.guru</strong> — miniDevToolkit adds next-5-runs preview, plain-English descriptions, 18 presets, and a visual field builder, while crontab.guru focuses on expression validation only. Compared to <strong>WorldTimeBuddy</strong> — miniDevToolkit's timezone converter is designed for single-date developer queries rather than meeting scheduling. All conversions run locally in your browser — no account or sign-up required.</p>
      </article>
    </div>
  );
}